/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx, css } from '@emotion/react'

import type { EuiBasicTableColumn } from '@elastic/eui'
import {
  useEuiTheme,
  EuiBasicTable,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIconTip,
  EuiText,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import {
  isCloudSpecificProjectRoleId,
  resourceRoleLabelByRoleId,
} from '@modules/role-assignments-lib/types'
import type { ResourceRoleId, ResourceType } from '@modules/role-assignments-lib/types'
import {
  ResourceTypeDisplay,
  getCloudAPIAccessLabel,
  predefinedLabels,
} from '@modules/role-assignments-lib/messages'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'
import { useConfig } from '@modules/cui/ConfigContext'
import { ApiAccessOptions } from '@modules/role-assignments-components/ApiAccessSelector/types'
import LimitedAccessTooltip from '@modules/role-assignments-components/ApiAccessSelector/LimitedAccessTooltip'

import type { FunctionComponent } from 'react'

export type AllProjectRoleAssignmentsTableItem<T extends ResourceType> = {
  resourceType: T
  roleIds: Array<ResourceRoleId<T>>
  apiAccess?: ApiAccessOptions
}

const AllProjectRoleAssignmentsTable: FunctionComponent<{
  items: Array<AllProjectRoleAssignmentsTableItem<ResourceType>>
  isApiKey: boolean
}> = ({ items, isApiKey }) => {
  const [_, flags] = useFlagsWhenLoaded()
  const isAdminConsole = useConfig('APP_NAME') === 'adminconsole'
  const customRolesEnabled = isAdminConsole || flags.serverlessCustomRoles
  const isUIAMEnabled = flags.uiamUi
  const { euiTheme } = useEuiTheme()

  return (
    <EuiBasicTable
      data-test-id='all-role-assignments-table'
      items={items}
      columns={getColumns(customRolesEnabled, isUIAMEnabled, isApiKey, euiTheme)}
    />
  )
}

const getColumns = (
  customRolesEnabled: boolean,
  isUIAMEnabled: boolean,
  isApiKey: boolean,
  euiTheme: ReturnType<typeof useEuiTheme>['euiTheme'],
): Array<EuiBasicTableColumn<AllProjectRoleAssignmentsTableItem<ResourceType>>> => {
  const columns: Array<EuiBasicTableColumn<AllProjectRoleAssignmentsTableItem<ResourceType>>> = [
    {
      name: <FormattedMessage id='roles-assignments.type' defaultMessage='Type' />,
      field: 'resourceType',
      render: (resourceType: ResourceType) => (
        <ResourceTypeDisplay resourceType={resourceType} plural={true} capitalize={true} />
      ),
      width: isUIAMEnabled ? '40%' : '50%',
    },
    {
      name: (
        <FormattedMessage
          id='roles-assignments.allAssignmentRoleColumn'
          defaultMessage='{count, plural, one {Role} other {Roles}}'
          values={{ count: customRolesEnabled ? 2 : 1 }}
        />
      ),
      field: 'roleIds',
      render: (roleIds: string[], tableItem) => {
        const rolesToProcess = customRolesEnabled ? roleIds : roleIds.slice(0, 1)
        const roleItems = rolesToProcess.map((roleId) => {
          const roleLabel = resourceRoleLabelByRoleId[roleId]
          const predefinedRoleLabels = predefinedLabels[tableItem.resourceType]

          return (
            <EuiFlexItem grow={false} key={tableItem.resourceType.concat(roleId)}>
              <FormattedMessage {...predefinedRoleLabels[roleLabel]} />
            </EuiFlexItem>
          )
        })
        return (
          <EuiFlexGroup direction='column' justifyContent='flexStart' gutterSize='xs'>
            {roleItems}
          </EuiFlexGroup>
        )
      },
      width: isUIAMEnabled ? '30%' : '50%',
    },
  ]

  if (isUIAMEnabled) {
    columns.push({
      name: <FormattedMessage id='roles-assignments.apiAccessColumn' defaultMessage='API Access' />,
      field: 'apiAccess',
      render: (apiAccess: ApiAccessOptions | undefined, tableItem) => {
        const containsNonCloudSpecificProjectRoles = tableItem.roleIds.some(
          (roleId) => !isCloudSpecificProjectRoleId(roleId),
        )
        const shouldShowTooltip =
          containsNonCloudSpecificProjectRoles && apiAccess === ApiAccessOptions.CloudApi

        return (
          <EuiFlexGroup direction='column' justifyContent='flexStart' gutterSize='xs'>
            {apiAccess ? (
              <EuiFlexGroup justifyContent='flexStart' gutterSize='s'>
                <EuiFlexItem grow={false}>
                  <FormattedMessage
                    {...getCloudAPIAccessLabel({
                      apiAccess,
                      isApiKey,
                    })}
                  />
                </EuiFlexItem>
                <EuiFlexItem grow={false} css={css({ alignSelf: 'center' })}>
                  {shouldShowTooltip && (
                    <EuiIconTip
                      type='warning'
                      color='warning'
                      content={<LimitedAccessTooltip isApiKey={isApiKey} />}
                      position='top'
                      iconProps={{
                        css: {
                          display: 'block',
                        },
                      }}
                      css={css({ backgroundColor: euiTheme.colors.backgroundBasePlain })}
                    />
                  )}
                </EuiFlexItem>
              </EuiFlexGroup>
            ) : (
              <EuiText size='s' color='subdued'>
                <FormattedMessage
                  id='roles-assignments.not-supported.access.label'
                  defaultMessage='Not supported'
                />
              </EuiText>
            )}
          </EuiFlexGroup>
        )
      },
      width: '30%',
    })
  }

  return columns
}

export default AllProjectRoleAssignmentsTable
